import type { PhaseCategory } from './cutTypes';

// Phase tips — ported from the demo's PHASE_TIPS catalog.
// Shown as a gold-gradient card during the live cut. Rotates every 5 minutes within a phase.
//
// Three tip arrays per category:
//   PHASE_TIPS         — shared baseline (everyone sees these)
//   PHASE_TIPS_NEW     — overlay for new barbers (more teaching / encouragement)
//   PHASE_TIPS_MASTER  — overlay for master barbers (more strategic / business)
// `pickTip(cat, cycleIdx, level)` interleaves the overlay into the shared array
// so rotation surfaces both kinds across the 5-min cycling window.

export type ExperienceLevel = 'new' | 'mid' | 'master';

export const PHASE_TIPS: Record<PhaseCategory, string[]> = {
  setup: [
    'Lock the cut in 30 seconds — length, fade, beard. Fewer surprises later.',
    'Ask about hair history before cutting — chemicals, last cut, problem areas.',
    'Confirm timeline — quick cut or full session? Sets the pace.',
    "Mirror their language back. They said 'tighter on the sides' — repeat it.",
  ],
  bulk: [
    'Heads down on the cut. Save the small talk for the line-up.',
    'Measure your guard before each pass. Wrong size = redo.',
    'Read the room — chatty client wants chat, quiet client wants quiet.',
    'Pace yourself. Bulk is the longest phase — bursting through it = mistakes.',
  ],
  fade: [
    'Use a credit card edge for the fade guideline. Sharp + clean every time.',
    'Check the blend from three angles — front, side, back.',
    'Cool blade = clean blend. Spray water if it gets hot.',
    "Step back every 90 seconds. The blend looks different from arm's length.",
  ],
  lineup: [
    'Check symmetry from the front mirror. Left side = right side.',
    "Slow down. Line-up is what they'll see in the mirror first.",
    'Sharpen your blade between line-ups. Dull blade = wavy lines.',
  ],
  finish: [
    'Lock the rebook NOW. Same time, 4 weeks out. Done before they stand up.',
    "Snap the after photo. Send it to their phone — they'll share it for you.",
    "Ask for a Google review while they're happy — \"Two minutes, big help.\"",
    'Mention the referral discount — bring a friend, save $5.',
  ],
  beard: [
    'Map the jawline first, fade up from there.',
    'Confirm beard line preference before you commit — too high or too low?',
    "Beard work = great photo opportunity. Ask while they're feeling sharp.",
  ],
  hottowel: [
    "Calm moment — confirm the next visit while they're relaxed.",
    "Quiet pause. Let the experience breathe — they'll remember this part.",
    'Hot towel = your reset moment. Take a breath before the next phase.',
  ],
  shave: [
    'First pass always WITH the grain. Second pass cleans up.',
    'Hot towel for 90 seconds before the first pass. Softens the hair.',
    'Stretch the skin taut with your free hand. Cleaner cut, fewer nicks.',
  ],
  // 'single' is the synthetic category for cuts where the barber turned
  // off "Use phases" — one big timer carries the whole cut. Tips are
  // generic pacing/quality nudges since we have no narrower phase context.
  single: [
    'Pace yourself — you have one timer, not five. Steady tempo beats sprinting.',
    'Mid-cut, glance at the time once. Lets you finish strong without the last-minute panic.',
    'Quality > speed. A clean cut at 5 min over target beats a rushed one on time.',
    'Read the room — chatty client wants chat, quiet client wants quiet.',
  ],
};

// New-barber overlay — more "here's why" + technique reinforcement + encouragement.
export const PHASE_TIPS_NEW: Partial<Record<PhaseCategory, string[]>> = {
  setup: [
    'Eye contact + smile when they sit down. The whole consult lands better when they feel seen first.',
    "If you're unsure about a length, err shorter — you can always trim more, but you can't add it back.",
  ],
  bulk: [
    'Anchor your wrist on the head. Floating wrists = wavy lines. This is the #1 fix for first-year barbers.',
    'Watch your elbow position — drop it for stability on the bulk passes.',
  ],
  fade: [
    'Practice the flicker motion at home with a comb. Wrist mechanics matter more than the clipper.',
    "Step back, look, step in, cut. Don't power through — pause and check.",
  ],
  lineup: [
    'Steady your hand by anchoring your pinky on their skin. Tension on the trimmer = stable line.',
    "If your line gets uneven, breathe. Don't try to fix it fast — that's how lines go from bad to worse.",
  ],
  finish: [
    "After the cape comes off, a real smile + 'how does it feel?' is worth more than any product upsell.",
    'Watch their face when they see the back. That reaction tells you what they actually think.',
  ],
  beard: [
    'Less is more on a first beard cut for any client. Cut conservatively, see if they ask for tighter.',
  ],
  hottowel: [
    "Use this minute to mentally walk through what's left of the cut. Pros plan during the towel.",
  ],
  shave: [
    'Hold the razor like a pencil for short, controlled passes. Long strokes are how nicks happen.',
  ],
};

// Master-barber overlay — more strategic / business-side / portfolio-optimization tips.
export const PHASE_TIPS_MASTER: Partial<Record<PhaseCategory, string[]>> = {
  setup: [
    "Pre-frame the upsell here: “Beard trim today, or just the cut?” Sets the ticket before the chair.",
    "Note their phone in their hand — they're likely going to post. Frame the cut for the photo.",
  ],
  bulk: [
    "Calendar opportunity: bring up their next appointment now while you're heads-down. Quiet ask, easy yes.",
    "If they're a regular, casually mention the new service you're rolling out. Test the price reaction.",
  ],
  fade: [
    'Photo opportunity in 3 minutes when the fade pops. Get the phone ready in your apron.',
    "When they comment on the fade, that's your social proof asking moment: 'Tag me, I'll repost.'",
  ],
  lineup: [
    "Their face is in the mirror. This is when you mention the referral program — they're feeling sharp.",
  ],
  finish: [
    "Lock the rebook + mention next quarter's price adjustment in the same breath. Confident, not apologetic.",
    'Premium clients: ask about the next big event in their life. Future appointments anchor to milestones.',
  ],
  beard: [
    "Beard work is your highest-margin add-on. Every beard finish should end with: “Same time next month?”",
  ],
  hottowel: [
    "Quiet moment — listen for what they're actually worried about this month. That's your retention signal.",
  ],
  shave: [
    'Premium service window. This is where you suggest a quarterly straight-shave package: 4 visits, $XX.',
  ],
};

// Map years_cutting (free-text) → experience level.
//   new    : 0-2 years
//   mid    : 3-9 years (default — also used when value is unparseable / missing)
//   master : 10+ years
// Accepts either a plain number string ("5") or label strings ("1-3 years", "10+", "<1 year").
export function getExperienceLevel(yearsCutting: string | null | undefined): ExperienceLevel {
  if (!yearsCutting) return 'mid';
  const normalized = yearsCutting.trim().toLowerCase();
  if (!normalized) return 'mid';

  // Plain number
  const numMatch = normalized.match(/^(\d+(?:\.\d+)?)$/);
  if (numMatch) return bucketByYears(parseFloat(numMatch[1]));

  // "<1" or "less than 1" → 0
  if (/<\s*1|less than 1/.test(normalized)) return 'new';

  // "10+" / "10 or more" / "10 plus"
  const plusMatch = normalized.match(/(\d+)\s*\+/);
  if (plusMatch) return bucketByYears(parseInt(plusMatch[1], 10));

  // Range like "1-3" / "1 to 3" / "1–3" — use the midpoint
  const rangeMatch = normalized.match(/(\d+)\s*[–\-to]+\s*(\d+)/);
  if (rangeMatch) {
    const lo = parseInt(rangeMatch[1], 10);
    const hi = parseInt(rangeMatch[2], 10);
    return bucketByYears((lo + hi) / 2);
  }

  // Fallback: try to grab any leading number
  const leadingNum = normalized.match(/^(\d+)/);
  if (leadingNum) return bucketByYears(parseInt(leadingNum[1], 10));

  return 'mid';
}

function bucketByYears(years: number): ExperienceLevel {
  if (years <= 2) return 'new';
  if (years >= 10) return 'master';
  return 'mid';
}

// Spanish translations of every tip in PHASE_TIPS / PHASE_TIPS_NEW / PHASE_TIPS_MASTER.
// Indexed positionally — tipsForCategory_es[cat][i] mirrors PHASE_TIPS[cat][i] etc.
// If a translation is missing for a given index, English falls through.
const PHASE_TIPS_ES: Record<PhaseCategory, string[]> = {
  setup: [
    'Cierra el corte en 30 segundos — largo, fade, barba. Menos sorpresas después.',
    'Pregunta por el historial del pelo antes de cortar — químicos, último corte, áreas problema.',
    'Confirma el tiempo — ¿corte rápido o sesión completa? Define el ritmo.',
    'Repite sus palabras. Si dijo "más corto a los lados", repítelo.',
  ],
  bulk: [
    'Cabeza al corte. Deja la plática para el line-up.',
    'Mide tu guarda antes de cada pasada. Tamaño equivocado = a empezar de nuevo.',
    'Lee el ambiente — cliente platicador quiere plática, cliente callado quiere silencio.',
    'Llévalo con calma. La fase de bulto es la más larga — apurarte = errores.',
  ],
  fade: [
    'Usa el filo de una tarjeta como guía del fade. Filoso y limpio cada vez.',
    'Revisa el blend desde tres ángulos — frente, lado, atrás.',
    'Cuchilla fría = blend limpio. Rocía agua si se calienta.',
    'Da un paso atrás cada 90 segundos. El blend se ve distinto desde lejos.',
  ],
  lineup: [
    'Verifica simetría desde el espejo del frente. Lado izquierdo = lado derecho.',
    'Despacio. El line-up es lo primero que verán en el espejo.',
    'Afila tu cuchilla entre line-ups. Cuchilla sin filo = líneas chuecas.',
  ],
  finish: [
    'Cierra la próxima cita YA. Mismo horario, en 4 semanas. Listo antes de que se paren.',
    'Toma la foto del después. Mándala a su teléfono — ellos la comparten por ti.',
    'Pide la reseña de Google ahora que están contentos — "Dos minutos, gran ayuda".',
    'Menciona el descuento por referido — trae a un amigo, ahorra $5.',
  ],
  beard: [
    'Marca el contorno de la mandíbula primero, luego sube el fade desde ahí.',
    'Confirma la línea de la barba antes de comprometerte — ¿muy alta o muy baja?',
    'La barba = excelente foto. Pídela mientras se sienten filosos.',
  ],
  hottowel: [
    'Momento tranquilo — confirma la próxima cita mientras están relajados.',
    'Pausa silenciosa. Deja respirar la experiencia — recordarán esta parte.',
    'Toalla caliente = tu reseteo. Respira hondo antes de la próxima fase.',
  ],
  shave: [
    'Primera pasada siempre A FAVOR del pelo. La segunda limpia.',
    'Toalla caliente 90 segundos antes de la primera pasada. Suaviza el vello.',
    'Estira la piel con la mano libre. Corte más limpio, menos cortes.',
  ],
  single: [
    'Lleva el ritmo — un solo cronómetro, no cinco. Constante mejor que apurado.',
    'A media tijera, mira el tiempo una vez. Terminas fuerte sin pánico al final.',
    'Calidad > velocidad. Un corte limpio 5 min tarde gana a uno apurado a tiempo.',
    'Lee al cliente — el conversador quiere plática, el callado quiere silencio.',
  ],
};

const PHASE_TIPS_NEW_ES: Partial<Record<PhaseCategory, string[]>> = {
  setup: [
    'Contacto visual + sonrisa cuando se sienten. La consulta entra mejor cuando se sienten vistos primero.',
    'Si dudas de un largo, mejor más corto — siempre puedes recortar más, pero no puedes pegar pelo.',
  ],
  bulk: [
    'Ancla tu muñeca en la cabeza. Muñecas flotantes = líneas chuecas. El arreglo #1 para barberos del primer año.',
    'Cuida la posición del codo — bájalo para estabilidad en las pasadas de bulto.',
  ],
  fade: [
    'Practica el movimiento de flicker en casa con un peine. La mecánica de muñeca importa más que la máquina.',
    'Atrás, mira, adelante, corta. No fuerces — pausa y revisa.',
  ],
  lineup: [
    'Estabiliza la mano apoyando el meñique en su piel. Tensión en la trimmer = línea estable.',
    'Si la línea sale desigual, respira. No la arregles rápido — así pasa de mal a peor.',
  ],
  finish: [
    'Después de quitar la capa, una sonrisa real + "¿cómo se siente?" vale más que cualquier upsell.',
    'Mira su cara cuando ven la parte de atrás. Esa reacción te dice qué piensan de verdad.',
  ],
  beard: [
    'Menos es más en una primera barba con cualquier cliente. Corta conservador, a ver si piden más cerrado.',
  ],
  hottowel: [
    'Usa este minuto para repasar mentalmente lo que falta del corte. Los pros planean en la toalla.',
  ],
  shave: [
    'Sostén la navaja como un lápiz para pasadas cortas y controladas. Las pasadas largas son cómo pasan los cortes.',
  ],
};

const PHASE_TIPS_MASTER_ES: Partial<Record<PhaseCategory, string[]>> = {
  setup: [
    'Pre-encuadra el upsell aquí: "¿Barba hoy, o solo el corte?" Define el ticket antes de la silla.',
    'Nota su teléfono en la mano — probablemente van a postear. Encuadra el corte para la foto.',
  ],
  bulk: [
    'Oportunidad de calendario: menciona la próxima cita ahora mientras estás concentrado. Pregunta tranquila, sí fácil.',
    'Si es regular, casualmente menciona el nuevo servicio que estás sacando. Prueba la reacción al precio.',
  ],
  fade: [
    'Oportunidad de foto en 3 minutos cuando el fade quede. Ten el teléfono listo en tu mandil.',
    'Cuando comenten el fade, ese es tu momento de pedir prueba social: "Etiquétame, lo reposteo".',
  ],
  lineup: [
    'Su cara está en el espejo. Aquí mencionas el programa de referidos — se sienten filosos.',
  ],
  finish: [
    'Cierra la próxima cita + menciona el ajuste de precio del próximo trimestre en la misma frase. Seguro, no apologético.',
    'Clientes premium: pregunta por el próximo evento grande de su vida. Las citas futuras anclan a hitos.',
  ],
  beard: [
    'La barba es tu add-on de mayor margen. Cada finish de barba debe terminar con: "¿Mismo horario el próximo mes?"',
  ],
  hottowel: [
    'Momento silencioso — escucha lo que de verdad les preocupa este mes. Esa es tu señal de retención.',
  ],
  shave: [
    'Ventana de servicio premium. Aquí sugieres un paquete trimestral de afeitado: 4 visitas, $XX.',
  ],
};

export function tipsForCategory(
  cat: PhaseCategory,
  level: ExperienceLevel = 'mid',
  language: 'en' | 'es' = 'en',
): string[] {
  const sharedSrc = language === 'es' ? PHASE_TIPS_ES : PHASE_TIPS;
  const overlaySrc =
    level === 'new'
      ? language === 'es'
        ? PHASE_TIPS_NEW_ES
        : PHASE_TIPS_NEW
      : level === 'master'
      ? language === 'es'
        ? PHASE_TIPS_MASTER_ES
        : PHASE_TIPS_MASTER
      : {};
  const shared = sharedSrc[cat] ?? PHASE_TIPS[cat] ?? PHASE_TIPS.setup;
  const overlay = overlaySrc[cat] ?? [];
  if (overlay.length === 0) return shared;
  // Interleave: overlay tips show first, then shared tips, so the new/master tone
  // hits early in the rotation but doesn't crowd out the shared coaching either.
  const out: string[] = [];
  const max = Math.max(overlay.length, shared.length);
  for (let i = 0; i < max; i++) {
    if (i < overlay.length) out.push(overlay[i]);
    if (i < shared.length) out.push(shared[i]);
  }
  return out;
}

export function pickTip(
  cat: PhaseCategory,
  cycleIdx: number,
  level: ExperienceLevel = 'mid',
  language: 'en' | 'es' = 'en',
): string {
  const arr = tipsForCategory(cat, level, language);
  if (arr.length === 0) return '';
  const idx = ((cycleIdx % arr.length) + arr.length) % arr.length;
  return arr[idx];
}
